import React from 'react';
import { ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card';
import { Badge } from '../Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../Table';
import { isKnownCorridor } from '../../data/transitCorridors';
import { currentTimeIn } from '../../utils/time';
import { ACCESS_METHOD_LABELS, type AccessMethod } from '../../utils/accessMethods';
import type { OriginCoverage, RestrictedAccessRow, RestrictedHit } from '../../types/traffic';

/** Signals that resolve a region when the time zone has been masked. */
const LOCALE_BASES = new Set(['secondary language', 'calendar', 'digit system']);
interface RestrictedJurisdictionsCardProps {
  /** Screening results computed across the full visitor population, in SQL. */
  hits: RestrictedHit[];
  listSize: number;
  coverage: OriginCoverage;
  uniqueVisitors: number;
  restrictedAccess: RestrictedAccessRow[];
}

/**
 * Traffic from jurisdictions the business cannot serve. Screening runs in the
 * database over every visitor — an earlier version cross-referenced the top-10
 * country lists in the browser, which silently hid any jurisdiction with only a
 * few visitors.
 */
export function RestrictedJurisdictionsCard({
  hits,
  listSize,
  coverage,
  uniqueVisitors,
  restrictedAccess
}: RestrictedJurisdictionsCardProps) {
  const isClean = hits.length === 0;
  const totalByIp = hits.reduce((sum, hit) => sum + hit.byIp, 0);
  const totalByRegion = hits.reduce((sum, hit) => sum + hit.byBrowserRegion, 0);
  const totalMasked = hits.reduce((sum, hit) => sum + hit.masked, 0);
  const totalLocaleOnly = hits.reduce((sum, hit) => sum + hit.byLocaleInternalsOnly, 0);
  const flagged = new Set(hits.map(hit => hit.code));
  const methodTotals = new Map<string, number>();
  for (const row of restrictedAccess) {
    if (!flagged.has(row.origin.trim().toUpperCase())) continue;
    if (row.method === 'unknown') continue;
    methodTotals.set(row.method, (methodTotals.get(row.method) ?? 0) + row.visitors);
  }
  const methodsByOrigin = Array.from(methodTotals.entries()).sort((a, b) => b[1] - a[1]).map(([method, visitors]) => `${ACCESS_METHOD_LABELS[method as AccessMethod] ?? method} (${visitors})`);
  const share = (value: number) => uniqueVisitors > 0 ? Math.min(100, Math.round(value / uniqueVisitors * 100)) : 0;
  const screenedByRegion = Math.min(coverage.withOrigin, uniqueVisitors);
  const screenedByIp = Math.min(coverage.withCountry, uniqueVisitors);
  return <Card data-id="element-355" data-mp="3gn9dsq17eskr">
      <CardHeader data-id="element-356" data-mp="3gmzluwwlmbcf">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-357" data-mp="32q41i8awcxh2">
          <CardTitle className="flex items-center gap-2" data-id="element-358" data-mp="32rxd820da7vx">
            <ShieldAlert className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-359" data-mp="36pv2t5y1uvzu" />
            Restricted jurisdictions
          </CardTitle>
          <Badge variant="outline" data-id="element-360" data-mp="1vc0zzqo8tl22">Screening signal</Badge>
        </div>
        <CardDescription data-id="element-361" data-mp="1vdbz3d6qu4mu">
          Traffic from territories where the business cannot operate, screened across every visitor
          by IP country and by the region the browser&rsquo;s own settings imply
        </CardDescription>
      </CardHeader>
      <CardContent data-id="element-362" data-mp="1xqxps7a5see6">
        {isClean ? <div className="text-xs leading-relaxed text-muted-foreground" data-id="element-363" data-mp="1xpwcrx9l1htt">
            <p className="font-medium text-foreground" data-id="element-364" data-mp="1ssw73ngnah38">No hits across {listSize} jurisdictions</p>
            <p className="mt-1.5" data-id="element-365" data-mp="152s49iba6lda">
              {screenedByRegion.toLocaleString()} of {uniqueVisitors.toLocaleString()} visitors (
              {share(screenedByRegion)}%) were screened by browser region, which needs no IP lookup.{' '}
              {screenedByIp.toLocaleString()} were also screened by IP country (
              {share(screenedByIp)}%). Every visitor is checked individually, not just the top
              countries.
            </p>
            <p className="mt-1.5" data-id="element-366" data-mp="hcfmzgu5bca1">
              Screening also reads locale internals — the calendar, digit system, and secondary
              accepted languages — so a visitor whose time zone is masked or forced to UTC can still
              resolve. Remaining blind spots: someone who changed those settings too, and unresolved
              IPs, which cannot be screened on that axis.
            </p>
          </div> : <>
            <div className="grid grid-cols-3 gap-3 sm:gap-4" data-id="element-367" data-mp="3uilgmsm895j3">
              <div data-id="element-368" data-mp="3c1yese2adar">
                <p className="font-mono text-xl tabular-nums text-foreground" data-id="element-369" data-mp="3ah5t8bb54fj">
                  {totalByIp.toLocaleString()}
                </p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-370" data-mp="8cg80evb9vaw">by IP country</p>
              </div>
              <div data-id="element-371" data-mp="cadzjock0thg">
                <p className="font-mono text-xl tabular-nums text-foreground" data-id="element-372" data-mp="hb1n201kqs6x">
                  {totalByRegion.toLocaleString()}
                </p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-373" data-mp="2e54vwqlndq66">by browser region</p>
              </div>
              <div data-id="element-374" data-mp="2j2wuu4kpmcjv">
                <p className="font-mono text-xl tabular-nums text-foreground" data-id="element-375" data-mp="2j4hozt343th7">
                  {totalMasked.toLocaleString()}
                </p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-376" data-mp="26rdy932h3rw8">masked — IP exits elsewhere</p>
              </div>
            </div>

            {totalLocaleOnly > 0 && <p className="mt-4 text-xs leading-relaxed text-muted-foreground" data-id="element-377" data-mp="1wurhapd9pkzh">
                <span className="font-medium text-foreground" data-id="element-378" data-mp="2uhzuzy711pqx">
                  {totalLocaleOnly.toLocaleString()} caught only by locale internals
                </span>{' '}
                — a calendar, digit system, or secondary accepted language. Their time zone resolved
                to nothing, so a zone-only screen would have missed them entirely.
              </p>}

            <div className="-mx-1 mt-6 overflow-x-auto px-1" data-id="element-379" data-mp="2gkl0if1x2rs3">
              <Table data-id="element-380" data-mp="19688m8qb366v">
                <TableHeader data-id="element-381" data-mp="192kf39kv2js5">
                  <TableRow data-id="element-382" data-mp="153bjs0a6aduh">
                    <TableHead data-id="element-383" data-mp="1520f7y8wl22b">Jurisdiction</TableHead>
                    <TableHead data-id="element-384" data-mp="16k8wghbfkng7">Basis</TableHead>
                    <TableHead className="text-right" data-id="element-385" data-mp="16lk10jcp9z8d">By IP</TableHead>
                    <TableHead className="text-right" data-id="element-386" data-mp="1bjv8infivxnf">By browser region</TableHead>
                    <TableHead data-id="element-387" data-mp="wm2szv9z7998">Resolved by</TableHead>
                    <TableHead data-id="element-388" data-mp="wljcmizz4sxb">Their local time now</TableHead>
                    <TableHead data-id="element-389" data-mp="11kmgw184qv48">Masked via</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="element-390" data-mp="nthif2gi08vh">
                  {hits.map(hit => <TableRow key={hit.code} data-id="element-391" data-mp="skhbtxqes8t8">
                      <TableCell className="whitespace-nowrap text-xs text-foreground" data-id="element-392" data-mp="sl9717g3g4iz">
                        {hit.name} ({hit.code})
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground" data-id="element-393" data-mp="iu4ueedw4vrb">{hit.basis}</TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums" data-id="element-394" data-mp="4uda5vws6j4v">
                        {hit.byIp.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums" data-id="element-395" data-mp="3mdkgwidaybaz">
                        {hit.byBrowserRegion.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground" data-id="element-396" data-mp="3hgkcsd00jimi">
                        {hit.bases.length > 0 ? <span className="flex flex-wrap gap-1" data-id="element-397" data-mp="3qfffvg7rgcll">
                            {hit.bases.map(basis => <span key={basis} className={LOCALE_BASES.has(basis) ? 'whitespace-nowrap font-medium text-foreground' : 'whitespace-nowrap'} data-id="element-398" data-mp="960kzj56flvn">
                                {basis}
                              </span>)}
                          </span> : '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs" data-id="element-399" data-mp="1ud8vle9pkfyt">
                        {hit.browserZones.length > 0 ? <span className="flex flex-col gap-0.5" data-id="element-400" data-mp="23b2r4mznf5kx">
                            {hit.browserZones.map(zone => <span key={zone} className="font-mono" data-id="element-401" data-mp="289cnh0x40c84">
                                <span className="text-foreground" data-id="element-402" data-mp="1anf7ba67cizc">{currentTimeIn(zone)}</span>{' '}
                                <span className="text-muted-foreground" data-id="element-403" data-mp="1flgsq1r3oudz">
                                  {zone.split('/').pop()?.replace(/_/g, ' ')}
                                </span>
                              </span>)}
                          </span> : <span className="font-mono text-muted-foreground" data-id="element-404" data-mp="rve4jwqb48kn">—</span>}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground" data-id="element-405" data-mp="wtfo0irna9wn">
                        {hit.maskedVia.length > 0 ? <span className="flex flex-wrap items-center gap-1.5" data-id="element-406" data-mp="kcmx9gr59ibf">
                            {hit.maskedVia.map(via => <span key={via} className="whitespace-nowrap" data-id="element-407" data-mp="fbyxo6ount5l">
                                {via}
                                {isKnownCorridor(hit.code, via) && <span className="ml-1 text-foreground" title="Known transit route" data-id="element-408" data-mp="6eokugwdvvyk">
                                    ◆
                                  </span>}
                              </span>)}
                          </span> : '—'}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 space-y-2 text-xs leading-relaxed text-muted-foreground" data-id="element-409" data-mp="5xorbv8h1k0v">
              <p data-id="element-410" data-mp="ylo8gc1xnvj">
                <span className="font-mono text-foreground" data-id="element-411" data-mp="z3tg2gozcfl">◆</span> marks a{' '}
                <span className="font-medium text-foreground" data-id="element-412" data-mp="fni4h0o9i778">known transit route</span> — a country
                traffic from that jurisdiction commonly passes through, by cross-border roaming, a
                leased line into an adjacent hub, or a VPN endpoint in the nearest large market. A
                corridor match makes the pairing consistent with normal routing; it is not evidence
                about any person, who may simply live there.
              </p>
              {methodsByOrigin.length > 0 && <p data-id="element-413" data-mp="3o2s0ps5h0i73">
                  Access methods seen from these regions:{' '}
                  <span className="text-foreground" data-id="element-414" data-mp="17ee7zaofmzhu">{methodsByOrigin.join(' · ')}</span>
                </p>}
            </div>
          </>}

        <div className="mt-6 space-y-2 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground" data-id="element-415" data-mp="13ct0k4uo3df1">
          <p className="font-medium text-foreground" data-id="element-416" data-mp="1ayy6pj3abuqy">This is not a sanctions control</p>
          <p data-id="element-417" data-mp="1ftlkamwr34qr">
            &ldquo;By browser region&rdquo; comes from the visitor&rsquo;s time zone and language —
            settings anyone can change in seconds, and which are wrong for travellers, relocated
            machines, and corporate laptops imaged elsewhere. It is a directional indicator of where
            demand comes from, not a determination about any person.
          </p>
          <p data-id="element-418" data-mp="1ylehktiurc4a">
            Real sanctions screening belongs in authenticated onboarding and payments, against a
            provider with denied-party lists — not on a maintenance page. Use this card to size
            exposure and to see whether IP-based geoblocking has a gap, then verify there.
          </p>
          <p data-id="element-419" data-mp="2ck4b5z6gqtnw">
            The list lives in the <span className="font-mono" data-id="element-420" data-mp="2hd74c67bw13k">restricted_jurisdictions</span> table
            and is a seed, not legal advice — programmes change and differ by regulator (OFAC, EU, UK
            OFSI, UN), so Compliance must own it.
          </p>
        </div>
      </CardContent>
    </Card>;
}